/**
 * Channel-connect wizards (contacts §7A.2 + E0; handoff §2.3) — REDESIGNED to
 * the assistant-hub §3.7 persuasion grammar (S2, operator refinement #7):
 * every dialog opens with the BENEFIT (headline + proof bullets, §3.7 copy
 * deck), then the journey stepper `① O canal do assistente → ② Você no
 * comando → ✓ Pronto`, then — and only then — the mechanics.
 *
 * MECHANICS ARE FROZEN (§3.8): the API calls, the 3s polling, the
 * `useChannelEndpoints` live detection, paired/bound dual-spelling acceptance,
 * the QR rendered VERBATIM from `qrDataUrl`, refetch-on-close. Telegram side 1
 * mirrors the users-guide SET-CHANNELS-TELEGRAM steps (collapsed beneath
 * "como funciona"); side 2 runs the one-time pairing-code handshake against
 * `channels.pair_owner.start/poll`. WhatsApp mirrors the shape (QR side +
 * code side). Email = the E0 connect shell until the §3.9 EmailSetupDialog
 * lands.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, PartyPopper, X } from "lucide-react";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { getChannelDetails } from "@/api/lx-openclaw-config";
import type { PairOwnerStartResult } from "@/types/contacts";
import { hasCommandDoor } from "@/lib/owner-channel-binding";
import { useChannelEndpoints, type IMChannel } from "@/hooks/useChannelEndpoints";
import { cn } from "@/lib/utils";
import { journeySteps } from "@/lib/channel-endpoint-state";
import { EmailSetupBlocks } from "@/components/agent/EmailSetupBlocks";
import { useAssistantIdentity, confirmedAssistantName } from "@/hooks/useAssistantIdentity";
import { WhatsAppPairingPanel } from "@/components/contacts/WhatsAppPairingPanel";
import { TelegramSetupWizard } from "@/components/settings/TelegramSetupWizard";

export type ConnectChannel = "telegram" | "whatsapp" | "email";

function methodPending(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /method|unknown|not.?found|unsupported/i.test(msg);
}

/** Per-channel accent for the dialog header — the same hue vocabulary as the
 *  tab rail, so the dialog visibly belongs to the tab that opened it. */
const CHANNEL_ACCENT: Record<ConnectChannel, { band: string; chip: string }> = {
  telegram: {
    band: "border-sky-400/50 bg-sky-50/80 dark:border-sky-400/30 dark:bg-sky-950/40",
    chip: "bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300",
  },
  whatsapp: {
    band: "border-emerald-400/50 bg-emerald-50/80 dark:border-emerald-400/30 dark:bg-emerald-950/40",
    chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300",
  },
  email: {
    band: "border-indigo-400/50 bg-indigo-50/80 dark:border-indigo-400/30 dark:bg-indigo-950/40",
    chip: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300",
  },
};

/** Side-2 pairing-code panel (shared by telegram + whatsapp). Mechanics
 *  identical to the pre-S2 version; presentation rebuilt per §3.8: benefit
 *  headline ("Só você comanda"), LARGE code, a one-glance direction line,
 *  warm waiting state, small celebration on success. */
function OwnerPairingPanel({
  channel,
  onBound,
}: {
  channel: "telegram" | "whatsapp";
  /** Fired once when the command door binds — lets the flow re-read the live
   *  endpoint state and advance to "complete" without waiting for the poll. */
  onBound?: () => void;
}) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const connected = connectionState === "connected";
  // The code is sent FROM the lawyer's own app TO the AGENT's account, so the
  // hint must name that destination (operator, 2026-07-24): the agent's WhatsApp
  // NUMBER, or the agent's Telegram @username (a bot has no phone). Read from
  // channel details; absent while unpaired or on an older runtime, in which case
  // the hint degrades to "para o assistente" with no coordinate rather than
  // inventing one. GET-only, non-destructive.
  const [agentCoord, setAgentCoord] = useState<string | null>(null);
  useEffect(() => {
    if (!rpc) return;
    let cancelled = false;
    void getChannelDetails(channel)
      .then((d) => {
        if (cancelled) return;
        if (d.channelId === "whatsapp" && d.identity?.phone) {
          setAgentCoord(d.identity.phone);
        } else if (d.channelId === "telegram" && d.identity?.username) {
          setAgentCoord(`@${d.identity.username}`);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [rpc, channel]);
  // Whether a command door already exists for this channel — REPLACE mode means
  // pairing now would REVOKE it. Read from the owner record (self-contained: the
  // dialog opens from several entry points, not all of which hold owner state).
  // GET-only; absent on an older runtime degrades to "no warning" rather than a
  // false one.
  const [alreadyBound, setAlreadyBound] = useState(false);
  useEffect(() => {
    // Gate on `connected`, not just `rpc`: the rpc object exists before the
    // socket is up, so an `identityGet` fired on `rpc` alone rejects silently and
    // — because `rpc`'s identity never changes on connect — never retries. Keying
    // on `connected` re-runs the read the moment the socket lands. (Same guard
    // the parent IdentityLayout's `refresh` uses.)
    if (!rpc || !connected) return;
    let cancelled = false;
    void rpc
      .identityGet()
      .then((res) => {
        if (!cancelled) setAlreadyBound(hasCommandDoor(res.owner, channel));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [rpc, connected, channel]);
  const [pairing, setPairing] = useState<PairOwnerStartResult | null>(null);
  const [state, setState] = useState<"idle" | "pending" | "bound" | "expired" | "unavailable">(
    "idle",
  );
  const [boundPeer, setBoundPeer] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // THE CODE'S REMAINING LIFE, ticking once a second while it is on screen
  // (ui-tester I-rt1-20260911-1811-localdev-18: the line read "expira em breve"
  // and never said when — on a flow that names concrete things everywhere else,
  // the bot in the code's hint and the 6 digits in the wrong-code reply).
  //
  // The source of truth is the code's OWN `expiresAt`, never a client-side copy
  // of the TTL: the 10-minute figure appears in no doc — it was only ever
  // OBSERVED in PAIRING.json — so hardcoding it here would invent a second
  // source for a server-owned value.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (state !== "pending" || !pairing) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [state, pairing]);
  // `null` = the expiry could not be read at all (absent or unparseable
  // `expiresAt`). That stays REPRESENTABLE and falls back to the original vague
  // copy rather than printing a number we do not have: a wrong countdown is
  // worse than an honest "soon".
  const msLeft = pairing ? Date.parse(pairing.expiresAt) - nowMs : Number.NaN;
  const secsLeft = Number.isFinite(msLeft) ? Math.max(0, Math.round(msLeft / 1000)) : null;
  const waitingText =
    secsLeft === null || secsLeft <= 0
      ? // The ZERO case lands here too: the countdown reaches 0 up to a poll
        // tick (3 s) before `state` flips to "expired", and "expira em 0
        // segundos" is a worse thing to show in that window than the original
        // vague line.
        t("channels.direction.waiting")
      : secsLeft >= 60
        ? t("channels.direction.waitingMin", { count: Math.ceil(secsLeft / 60) })
        : t("channels.direction.waitingSec", { count: secsLeft });

  const stopPolling = useCallback(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = null;
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  async function start() {
    if (!rpc) return;
    try {
      const res = await rpc.pairOwnerStart({ channel });
      setPairing(res);
      setState("pending");
      stopPolling();
      pollTimer.current = setInterval(() => {
        void rpc
          .pairOwnerPoll({ channel })
          .then((poll) => {
            // Accept BOTH spellings: the runtime says "paired", this mirror
            // historically guessed "bound". Matching only one strands the
            // dialog on a completed pairing.
            if (poll.state === "paired" || poll.state === "bound") {
              setState("bound");
              setBoundPeer(poll.peerId ?? null);
              stopPolling();
              onBound?.();
            } else if (poll.state === "expired") {
              setState("expired");
              stopPolling();
            }
          })
          .catch(() => undefined);
      }, 3000);
    } catch (err) {
      if (methodPending(err)) setState("unavailable");
    }
  }

  return (
    <div
      className="rounded-lg border border-cream-200/80 bg-white/60 p-4 dark:border-coffee-700/70 dark:bg-coffee-950/40"
      data-testid={`pair_owner_${channel}`}
    >
      {/* §3.7: the two-endpoint rule told as a BENEFIT, never an amber warning. */}
      <h4 className="font-serif-display text-base text-coffee-900 dark:text-cream-100">
        {t("channels.benefit.ownerPairing.headline")}
      </h4>
      <p className="mb-3 mt-1 text-body-sm text-coffee-600 dark:text-cream-400">
        {t("channels.benefit.ownerPairing.body")}
      </p>
      {state === "idle" && alreadyBound && (
        <p
          className="mb-2 rounded-md bg-cream-100 p-2 text-xs text-coffee-600 dark:bg-coffee-800/60 dark:text-cream-400"
          data-testid="pair_owner_replace_warning"
        >
          {t(
            "channels.ownerReplaceWarning",
            "Já existe uma conta de comando vinculada neste canal. Parear uma nova a SUBSTITUI — a conta atual deixa de comandar o assistente.",
          )}
        </p>
      )}
      {state === "idle" && (
        <button
          type="button"
          onClick={() => void start()}
          className="rounded-md bg-gold-500 px-4 py-1.5 text-sm font-semibold text-coffee-900 transition-colors hover:bg-gold-400"
          data-testid="pair_owner_start"
        >
          {alreadyBound
            ? t("channels.generateCodeReplace", "Gerar código para trocar de conta")
            : t("channels.generateCode", "Gerar código de pareamento")}
        </button>
      )}
      {state === "unavailable" && (
        <p className="rounded bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300" data-testid="pair_owner_pending_backend">
          {t(
            "channels.pairingPendingBackend",
            "O handshake de pareamento ainda está sendo ativado no seu runtime — volte em breve.",
          )}
        </p>
      )}
      {state === "pending" && pairing && (
        <div className="space-y-2">
          {/* THE code — the one thing to read across the room (§3.8). */}
          <div
            className="rounded-xl border border-gold-400/50 bg-cream-50 px-4 py-5 text-center font-mono text-4xl font-semibold tracking-[0.35em] text-coffee-900 dark:border-gold-500/40 dark:bg-coffee-900 dark:text-cream-50"
            data-testid="pair_owner_code"
          >
            {pairing.code}
          </div>
          {/* One-glance direction: from YOUR app → to the assistant. */}
          <p
            className="flex items-center justify-center gap-1.5 text-body-sm text-coffee-700 dark:text-cream-300"
            data-testid="pair_owner_send_hint"
          >
            <span className="font-medium">{t("channels.direction.fromYourApp")}</span>
            <ArrowRight size={14} aria-hidden className="shrink-0 text-gold-600" />
            <span className="font-medium">
              {agentCoord
                ? t("channels.direction.toAssistantCoord", { coord: agentCoord })
                : t("channels.direction.toAssistant")}
            </span>
          </p>
          <p className="text-center text-xs text-coffee-500 dark:text-cream-500">
            {waitingText}
          </p>
        </div>
      )}
      {state === "bound" && (
        <p
          className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm font-medium text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
          data-testid="pair_owner_bound"
        >
          <PartyPopper size={16} aria-hidden className="shrink-0" />
          <span>
            {t("channels.bound", "Identidade verificada e vinculada ao seu registro ✓")}
            {boundPeer && <code className="ml-1 text-xs">{boundPeer}</code>}
          </span>
        </p>
      )}
      {state === "expired" && (
        <p className="rounded bg-red-500/10 p-2 text-xs text-red-700 dark:text-red-300">
          {t(
            "channels.expired",
            "O código expirou (ou outro remetente tentou usá-lo — nesse caso ele foi estacionado como desconhecido). Gere um novo.",
          )}{" "}
          <button type="button" className="underline" onClick={() => void start()}>
            {t("channels.regenerate", "Gerar novo")}
          </button>
        </p>
      )}
    </div>
  );
}

/**
 * The journey stepper (§3.8) — replaces the bare ✓/• endpoint rows AND the
 * amber two-endpoint warning with one friendly picture of the whole trip:
 * `① O canal do assistente → ② Você no comando → ✓ Pronto`. Same live data
 * (`ep1`/`ep2`), told as a journey instead of a checklist.
 */
function JourneyStepper({
  channel,
  ep1,
  ep2,
}: {
  channel: IMChannel;
  ep1: boolean | null;
  ep2: boolean | null;
}) {
  const { t } = useTranslation();
  // The current step is NOT told by colour alone (WCAG 1.4.1, S10 triage
  // 2026-09-11): an ordered list, `aria-current="step"` on the current item, a
  // visually-hidden state per step, and a visible ring on the current circle.
  const states = journeySteps(ep1, ep2);
  const labels = [
    t("channels.stepper.step1"),
    t("channels.stepper.step2"),
    t("channels.stepper.done"),
  ];
  return (
    <ol
      aria-label={t("channels.stepper.label")}
      className="flex items-center justify-center gap-1.5 py-1"
      data-testid={`endpoint-status-${channel}`}
    >
      {labels.map((label, i) => {
        const state = states[i];
        const done = state === "done";
        const current = state === "current";
        return (
          <li
            key={i}
            aria-current={current ? "step" : undefined}
            className="flex items-center gap-1.5"
            data-testid="endpoint-status-row"
            data-state={state}
          >
            <span
              data-done={done ? "true" : "false"}
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-colors",
                done
                  ? "bg-emerald-500 text-white"
                  : current
                    ? "bg-gold-500 text-coffee-900 ring-2 ring-gold-600 ring-offset-2 ring-offset-cream-50 dark:ring-gold-400 dark:ring-offset-coffee-900"
                    : "bg-cream-200 text-coffee-500 dark:bg-coffee-800 dark:text-cream-500",
              )}
              aria-hidden
            >
              {done ? <Check size={13} /> : i === 2 ? "✓" : i + 1}
            </span>
            <span
              className={cn(
                "text-xs",
                done
                  ? "font-medium text-emerald-700 dark:text-emerald-300"
                  : current
                    ? "font-semibold text-coffee-800 dark:text-cream-200"
                    : "text-coffee-500 dark:text-cream-500",
              )}
            >
              {label}
              <span className="sr-only"> — {t(`channels.stepper.state.${state}`)}</span>
            </span>
            {i < labels.length - 1 && (
              <span
                aria-hidden
                className="h-px w-5 shrink-0 bg-cream-300 dark:bg-coffee-700"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The two-endpoint connect FLOW for an IM channel — reflects the LIVE state
 * (operator UX feedback 2026-07-26): a scanned QR / paired bot flips endpoint 1
 * to ✓ and the flow advances to endpoint 2; both ✓ shows "complete" instead of
 * a static re-pair prompt. `endpoint1` is the channel's own step-1 UI (the QR
 * panel for WhatsApp, the BotFather panel for Telegram). Detection keys
 * off `channels.status`, never `channelComplete` (see `useChannelEndpoints`).
 */
function IMConnectFlow({
  channel,
  endpoint1,
  onClose,
  intent = "connect",
}: {
  channel: IMChannel;
  /** See `ChannelConnectDialog`'s own `intent` docblock — a RETRADE must reach
   *  the credential door even on a channel that is already complete, because
   *  "already complete" is precisely the state a rotation starts from. */
  intent?: "connect" | "retrade";
  /** The channel's step-1 UI. A render function receives the flow's live
   *  `refresh` so a step-1 action that changes endpoint state (e.g. Telegram's
   *  inline token-paste wizard on apply) can advance the status rows without a
   *  second `useChannelEndpoints` instance. A plain node is still accepted
   *  (WhatsApp's QR panel needs no such hook). */
  endpoint1: ReactNode | ((refresh: () => void) => ReactNode);
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { ep1, ep2, refresh } = useChannelEndpoints(channel, { enabled: true });
  /**
   * ⚖ A retrade intent is CONSUMED the moment the door it opened is used.
   *
   * 🚨 Without this the intent outlives its own purpose: it is a static prop, so
   * after a SUCCESSFUL rotation the not-complete branch kept winning and the
   * lawyer who had just finished was shown "cole o token aqui" again, with the
   * channel reading connected the whole time. Measured by `tester-galvao`
   * 2026-09-13 immediately after the two-gate fix — a direct consequence of it,
   * and exactly the residue a fix like this produces.
   *
   * ⚠ Consumed on the endpoint-1 APPLY, not on a poll going green: `ep1` can
   * already be true throughout a retrade (that is the whole point), so
   * completeness cannot tell us the rotation happened. The apply callback can.
   */
  const [retradeSatisfied, setRetradeSatisfied] = useState(false);
  const effectiveIntent = intent === "retrade" && !retradeSatisfied ? "retrade" : "connect";
  const complete = ep1 === true && ep2 === true;
  const endpoint1Node =
    typeof endpoint1 === "function"
      ? endpoint1(() => {
          setRetradeSatisfied(true);
          refresh();
        })
      : endpoint1;

  return (
    <div className="space-y-3">
      <JourneyStepper channel={channel} ep1={ep1} ep2={ep2} />

      {ep1 === null ? (
        // First live read still pending — don't flash the QR / owner panels for
        // a channel that may already be complete; the stepper above already
        // shows the journey.
        <p
          className="px-1 py-2 text-center text-xs text-coffee-500 dark:text-cream-500"
          data-testid={`connect-checking-${channel}`}
        >
          {t("channels.checkingState", "Verificando o estado do canal…")}
        </p>
      ) : complete && effectiveIntent !== "retrade" ? (
        <div
          className="space-y-2 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-center dark:border-emerald-700 dark:bg-emerald-950/40"
          data-testid={`connect-complete-${channel}`}
        >
          <p className="flex items-center justify-center gap-2 text-sm font-medium text-emerald-800 dark:text-emerald-200">
            <PartyPopper size={16} aria-hidden className="shrink-0" />
            {t(
              "channels.bothDone",
              "Tudo pronto ✓ — este canal está completo. O assistente atende por aqui e você pode comandá-lo pelo seu número.",
            )}
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid={`connect-done-${channel}`}
            className="rounded-md border border-emerald-600 bg-emerald-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-emerald-700"
          >
            {t("channels.close", "Fechar")}
          </button>
        </div>
      ) : (
        <>
          {/* Endpoint 1 — hidden the moment it goes live, so the lawyer isn't
              offered a "generate QR" for a door that is already up.
              ⚖ EXCEPT on a RETRADE, where a live endpoint 1 is exactly what the
              lawyer came to replace. Hiding it here is the second half of the
              rotate dead end: even past the `complete` branch, the credential
              door stayed hidden because endpoint 1 was healthy. */}
          {(ep1 !== true || effectiveIntent === "retrade") && endpoint1Node}
          {/* ⚖ Not on a retrade: "etapa 1 concluída, falta a etapa 2" is wrong
              advice when step 1 is the step being redone. */}
          {effectiveIntent !== "retrade" && ep1 === true && ep2 !== true && (
            <div
              className="rounded-lg border border-emerald-300 bg-emerald-50 p-2.5 text-center text-xs text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
              data-testid={`ep1-done-${channel}`}
            >
              {t(
                "channels.ep1DoneAdvance",
                "Etapa 1 concluída ✓ — o assistente já tem o próprio canal. Falta a etapa 2: pareie o SEU número como porta de comando, abaixo.",
              )}
            </div>
          )}
          {/* Endpoint 2 — the command door. `onBound` advances to "complete" the
              instant it pairs, without waiting for the next poll tick. */}
          {ep2 !== true && <OwnerPairingPanel channel={channel} onBound={refresh} />}
        </>
      )}
    </div>
  );
}

/**
 * Telegram endpoint-1 (the assistant's bot via BotFather) — §3.8: benefit
 * framing first, the BotFather cheat-sheet COLLAPSED beneath "como funciona".
 * The token PASTE happens inline — `TelegramSetupWizard` launches right here
 * (paste → verify via `bot.getMe()` → optional test → apply/roll), instead of
 * sending the lawyer off to Settings and losing the connect flow.
 *
 * The wizard is normally hosted inside `<OpenclawSettingsTabs/>`; embedded here
 * it has no settings context, so we hand it `onApplied` = the flow's `refresh`
 * so a successful pair re-reads the endpoint status rows. (Endpoint 1 only
 * flips ✓ once the runtime rolls with the new bot-token — `requiresRoll:true`
 * for Telegram — so the row may stay pending until that restart lands; the
 * refresh reflects it on the next live read.)
 *
 * ⚠ CORRECTED 2026-09-13 — the parenthesis above said endpoint 1 only flips ✓
 * "once the runtime rolls with the new bot-token — `requiresRoll:true` for
 * Telegram". That is the retracted always-roll claim, and this was its FOURTH
 * site (two in `lx-openclaw-config.ts`, one in `TelegramSetupWizard`'s header,
 * this one). Since Option A a pair usually needs NO roll: read `requiresRoll`
 * and `activation.state` from the pair response rather than assuming either way.
 */
function TelegramBotSidePanel({ onApplied }: { onApplied: () => void }) {
  const { t } = useTranslation();
  const [wizardOpen, setWizardOpen] = useState(false);
  // ⚖ Step 2 hands the lawyer a name he CHOSE, never one the product picked for
  // him. `confirmedAssistantName` is null for all FIVE un-confirmed states —
  // including a STORED name equal to the platform default — and that null is
  // the safety property, not a nicety. See its docblock for why the obvious
  // accessor (`assistantDisplayName`) would instruct the Alexandre→Alex
  // collision this fix exists to close.
  const { assistant, loading, backendPending } = useAssistantIdentity();
  const chosenName = confirmedAssistantName(assistant, { loading, backendPending });

  return (
    <div
      className="rounded-lg border border-cream-200/80 bg-white/60 p-4 dark:border-coffee-700/70 dark:bg-coffee-950/40"
      data-testid="telegram_bot_side"
    >
      <h4 className="font-serif-display text-base text-coffee-900 dark:text-cream-100">
        {t("channels.tgSide1.headline")}
      </h4>
      <p className="mb-3 mt-1 text-body-sm text-coffee-600 dark:text-cream-400">
        {t("channels.tgSide1.body")}
      </p>
      <button
        type="button"
        onClick={() => setWizardOpen(true)}
        className="rounded-md bg-gold-500 px-4 py-1.5 text-sm font-semibold text-coffee-900 transition-colors hover:bg-gold-400"
        data-testid="telegram_paste_token"
      >
        {t("channels.pasteBotToken", "Colar token do bot")}
      </button>
      {/* The mechanism, tucked away until asked for (§3.7: mechanics never
          above the fold). Mirrors the users-guide SET-CHANNELS-TELEGRAM steps
          — ask the assistant "como conecto o Telegram?" for the walkthrough. */}
      <details className="mt-3 text-xs text-coffee-600 dark:text-cream-400">
        <summary className="cursor-pointer select-none font-medium text-coffee-700 hover:text-coffee-900 dark:text-cream-300 dark:hover:text-cream-100">
          {t("channels.tgSide1.how")}
        </summary>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          <li>{t("channels.tgStep1", "No Telegram, abra o @BotFather e envie /newbot.")}</li>
          <li data-testid="tg_step2" data-named={chosenName ? "1" : "0"}>
            {chosenName
              ? t("channels.tgStep2Named", {
                  name: chosenName,
                  defaultValue: "Dê o nome “{{name}}” ao bot — é o nome do seu assistente.",
                })
              : t(
                  "channels.tgStep2",
                  "Dê um nome ao bot — o nome do seu assistente (sem “Dr./Dra.” nem OAB, e nunca o nome de uma pessoa real).",
                )}
          </li>
          <li>{t("channels.tgStep3", "Copie o token que o BotFather devolve.")}</li>
          <li>{t("channels.tgStep4Inline", "Cole o token aqui mesmo — o assistente valida o token na hora:")}</li>
        </ol>
        <p className="mt-2 text-[11px] text-coffee-500 dark:text-cream-500">
          {t(
            "channels.guideMirrorNote",
            "Passo a passo completo: pergunte ao assistente “como conecto o Telegram?”.",
          )}
        </p>
      </details>
      {/* Inline token-paste wizard. No settings context here → `onApplied`
          drives the post-apply re-read. On close we also refresh, so a pair
          that completed without hitting step-5 apply still updates the rows. */}
      <TelegramSetupWizard
        open={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          onApplied();
        }}
        onApplied={onApplied}
      />
    </div>
  );
}

/** The §3.9 blocks with their own identity read — this dialog opens from
 *  entry points that hold no identity state (e.g. `/identity?connect=email`). */
function EmailSetupBody() {
  const { assistant, owner, refresh } = useAssistantIdentity();
  return (
    <div data-testid="email_connect_shell">
      <EmailSetupBlocks assistant={assistant} owner={owner} onChanged={refresh} />
    </div>
  );
}

export function ChannelConnectDialog({
  channel,
  onClose,
  intent = "connect",
}: {
  channel: ConnectChannel;
  /** Historically opened the dialog at a specific step; now superseded by
   *  `IMConnectFlow`'s live auto-detection (kept for caller compatibility). */
  step?: string;
  onClose: () => void;
  /**
   * ⚖ Why the caller's INTENT has to reach this dialog.
   *
   * 🚨 Without it, "I want to trade a working credential" and "I opened a
   * channel that is already finished" are indistinguishable here, because the
   * only thing consulted was endpoint completeness. Measured live 2026-09-13
   * (`tester-galvao`): on a HEALTHY Telegram channel the control labelled
   * **"Trocar token"** opened a terminal "Tudo pronto ✓" panel whose only
   * actions were Fechar / Agora não — `telegram_paste_token` ABSENT, on two
   * independent passes, controlled against the SAME control an hour earlier
   * when the channel was broken and it DID expose the token door.
   *
   * ⇒ The one state that affordance exists for — `ChannelDetailsPanel`'s own
   * docblock calls `"rotate"` *"a connected channel: trading a working
   * credential"* — was the one this dialog refused to serve. To rotate, a
   * lawyer had to BREAK his channel first (Desconectar, which deletes
   * `bot-token.txt`, or revoke upstream so the local token starts failing).
   * Today's real scenario is a LEAKED token, where the safe order is to enter
   * the replacement FIRST — and that order dead-ended with the leaked
   * credential still serving.
   */
  intent?: "connect" | "retrade";
}) {
  const { t } = useTranslation();
  const accent = CHANNEL_ACCENT[channel];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="channel_connect_dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl dark:bg-coffee-900">
        {/* §3.7 header: the channel's accent hue + the BENEFIT headline —
            what the lawyer GAINS — with the proof bullets right under it.
            The mechanism vocabulary never appears up here. */}
        <div className={cn("space-y-2 border-b px-6 py-5", accent.band)}>
          <div className="flex items-start gap-2">
            <h3 className="min-w-0 flex-1 font-serif-display text-xl leading-snug text-coffee-900 dark:text-cream-100">
              {t(`channels.benefit.${channel}.headline`)}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              title={t("common.close")}
              className="shrink-0 rounded-lg p-1.5 text-coffee-500 transition-colors hover:bg-white/60 hover:text-coffee-800 dark:text-cream-400 dark:hover:bg-coffee-800 dark:hover:text-cream-100"
              data-testid="channel_connect_close"
            >
              <X size={16} />
            </button>
          </div>
          <ul className="space-y-1">
            {([1, 2, 3] as const).map((n) => {
              const key = `channels.benefit.${channel}.proof${n}`;
              const text = t(key, { defaultValue: "" });
              if (!text) return null;
              return (
                <li
                  key={n}
                  className="flex items-start gap-2 text-body-sm text-coffee-700 dark:text-cream-300"
                >
                  <span aria-hidden className={cn("mt-1 h-1.5 w-1.5 shrink-0 rounded-full", "bg-gold-500")} />
                  <span>{text}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="space-y-3 px-6 py-4">
          {channel === "telegram" && (
            <IMConnectFlow
              channel="telegram"
              onClose={onClose}
              intent={intent}
              // Render-function endpoint1: the inline token-paste wizard needs the
              // flow's live `refresh` so a successful pair advances the status rows.
              endpoint1={(refresh) => <TelegramBotSidePanel onApplied={refresh} />}
            />
          )}

          {channel === "whatsapp" && (
            // Side 1 — the assistant's own number, paired by QR (fetched from the
            // runtime, never round-tripped through the concierge — see
            // `WhatsAppPairingPanel`'s docblock). The flow hides it the moment the
            // link goes live and advances to endpoint 2.
            <IMConnectFlow
              channel="whatsapp"
              onClose={onClose}
              intent={intent}
              endpoint1={<WhatsAppPairingPanel />}
            />
          )}

          {/* The old E0-pending shell is RETIRED (§3.9, S2): e-mail is not a
              connect flow — the mailbox is provisioned. This entry now shows
              the same three-block setup the hero's e-mail pencil opens. */}
          {channel === "email" && <EmailSetupBody />}

          {/* §3.7 footer: declining is a first-class, guilt-free choice. */}
          <div className="flex justify-end border-t border-cream-200/70 pt-3 dark:border-coffee-800/60">
            <button
              type="button"
              onClick={onClose}
              data-testid="channel_connect_not_now"
              className="rounded-md px-3 py-1.5 text-body-sm text-coffee-600 transition-colors hover:bg-cream-100 dark:text-cream-400 dark:hover:bg-coffee-800"
            >
              {t("channels.notNow")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
